import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Autocomplete,
  Grid,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Divider,
  Alert
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Close,
  Book,
  Settings
} from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Context colors mapping - will be populated from API
const getContextColor = (contextName, contexts) => {
  const context = contexts.find(c => c.name === contextName);
  return context?.color || 'default';
};

const Glossary = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [firstLetters, setFirstLetters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContext, setSelectedContext] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [formData, setFormData] = useState({
    term: '',
    definition: '',
    context: [],
    example: '',
    source: '',
    relatedTerms: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [relatedTermInput, setRelatedTermInput] = useState('');
  const [contexts, setContexts] = useState([]);
  const [contextsLoading, setContextsLoading] = useState(true);
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [contextFormOpen, setContextFormOpen] = useState(false);
  const [contextFormData, setContextFormData] = useState({ name: '', color: 'default', order: 0 });
  const [editingContext, setEditingContext] = useState(null);
  const [deletingContext, setDeletingContext] = useState(null);

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

  useEffect(() => {
    fetchContexts();
    fetchEntries();
  }, [pagination.page, searchQuery, selectedContext, selectedLetter]);

  useEffect(() => {
    fetchEntries();
  }, [contexts]);

  const fetchContexts = async () => {
    try {
      setContextsLoading(true);
      const response = await axios.get(`${API_URL}/glossary/contexts`);
      const contexts = response.data || [];
      setContexts(contexts);
      
      // If no contexts exist, show info message (only once)
      if (contexts.length === 0 && isSuperAdmin) {
        // Don't show toast on initial load, just log
        console.info('No contexts found. SUPER_ADMIN can add contexts using "Manage Contexts" button.');
      }
    } catch (error) {
      console.error('Error fetching contexts:', error);
      // Don't show error toast - the API should return default contexts even on error
      // Set empty array as last resort so UI doesn't break
      setContexts([]);
    } finally {
      setContextsLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedContext && { context: selectedContext }),
        ...(selectedLetter && { letter: selectedLetter })
      };

      const response = await axios.get(`${API_URL}/glossary`, { params });
      setEntries(response.data.entries);
      setPagination(response.data.pagination);
      setFirstLetters(response.data.firstLetters);
    } catch (error) {
      console.error('Error fetching glossary entries:', error);
      showToast('Failed to load glossary entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on search
  };

  const handleContextChange = (event, newValue) => {
    setSelectedContext(newValue);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter
  };

  const handleLetterClick = (letter) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null); // Toggle off if already selected
    } else {
      setSelectedLetter(letter);
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    }
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleViewDetails = async (entryId) => {
    try {
      const response = await axios.get(`${API_URL}/glossary/${entryId}`);
      setSelectedEntry(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error fetching entry details:', error);
      showToast('Failed to load entry details', 'error');
    }
  };

  const handleAddClick = () => {
    setFormData({
      term: '',
      definition: '',
      context: [],
      example: '',
      source: '',
      relatedTerms: []
    });
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleEditClick = (entry) => {
    setFormData({
      term: entry.term,
      definition: entry.definition,
      context: entry.context || [],
      example: entry.example || '',
      source: entry.source || '',
      relatedTerms: entry.relatedTerms || []
    });
    setSelectedEntry(entry);
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API_URL}/glossary/${entryToDelete._id}`);
      showToast('Glossary entry deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      showToast(error.response?.data?.message || 'Failed to delete entry', 'error');
    }
  };

  const handleFormSubmit = async () => {
    // Validation
    const errors = {};
    if (!formData.term.trim()) {
      errors.term = 'Term is required';
    }
    if (!formData.definition.trim()) {
      errors.definition = 'Definition is required';
    }
    if (!formData.context || formData.context.length === 0) {
      errors.context = 'At least one context is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (selectedEntry) {
        // Update
        await axios.put(`${API_URL}/glossary/${selectedEntry._id}`, formData);
        showToast('Glossary entry updated successfully', 'success');
      } else {
        // Create
        await axios.post(`${API_URL}/glossary`, formData);
        showToast('Glossary entry created successfully', 'success');
      }
      setEditDialogOpen(false);
      setSelectedEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save entry';
      showToast(errorMessage, 'error');
      if (error.response?.data?.existingId) {
        setFormErrors({ term: errorMessage });
      }
    }
  };

  const handleAddRelatedTerm = () => {
    if (relatedTermInput.trim() && !formData.relatedTerms.includes(relatedTermInput.trim())) {
      setFormData(prev => ({
        ...prev,
        relatedTerms: [...prev.relatedTerms, relatedTermInput.trim()]
      }));
      setRelatedTermInput('');
    }
  };

  const handleRemoveRelatedTerm = (term) => {
    setFormData(prev => ({
      ...prev,
      relatedTerms: prev.relatedTerms.filter(t => t !== term)
    }));
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Context Management Handlers
  const handleAddContextClick = () => {
    setContextDialogOpen(true);
    setContextFormOpen(false);
  };

  const handleAddNewContext = () => {
    // Close the list dialog first, then open the form dialog
    setContextDialogOpen(false);
    setTimeout(() => {
      setContextFormData({ name: '', color: 'default', order: contexts.length });
      setEditingContext(null);
      setContextFormOpen(true);
    }, 100);
  };

  const handleEditContextClick = (context) => {
    // Close the list dialog first, then open the form dialog
    setContextDialogOpen(false);
    setTimeout(() => {
      setContextFormData({
        name: context.name,
        color: context.color || 'default',
        order: context.order || 0
      });
      setEditingContext(context);
      setContextFormOpen(true);
    }, 100);
  };

  const handleDeleteContextClick = (context) => {
    setDeletingContext(context);
  };

  const handleDeleteContextConfirm = async () => {
    try {
      await axios.delete(`${API_URL}/glossary/contexts/${deletingContext._id}`);
      showToast('Context deleted successfully', 'success');
      setDeletingContext(null);
      fetchContexts();
    } catch (error) {
      console.error('Error deleting context:', error);
      showToast(error.response?.data?.message || 'Failed to delete context', 'error');
    }
  };

  const handleContextFormSubmit = async () => {
    if (!contextFormData.name.trim()) {
      showToast('Context name is required', 'error');
      return;
    }

    try {
      if (editingContext) {
        await axios.put(`${API_URL}/glossary/contexts/${editingContext._id}`, contextFormData);
        showToast('Context updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/glossary/contexts`, contextFormData);
        showToast('Context created successfully', 'success');
      }
      setContextDialogOpen(false);
      setContextFormOpen(false);
      setEditingContext(null);
      setContextFormData({ name: '', color: 'default', order: 0 });
      fetchContexts();
    } catch (error) {
      console.error('Error saving context:', error);
      showToast(error.response?.data?.message || 'Failed to save context', 'error');
    }
  };

  const contextOptions = contexts.map(c => c.name);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Aviation Glossary
        </Typography>
        <Stack direction="row" spacing={2}>
          {isSuperAdmin && (
            <>
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleAddContextClick}
              >
                Manage Contexts
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddClick}
              >
                Add Entry
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            placeholder="Search terms or definitions..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Autocomplete
              options={contextOptions}
              value={selectedContext}
              onChange={handleContextChange}
              loading={contextsLoading}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Context" size="small" sx={{ minWidth: 200 }} />
              )}
              clearOnEscape
            />
            {selectedContext && (
              <Chip
                label={`Context: ${selectedContext}`}
                onDelete={() => setSelectedContext(null)}
                color="primary"
              />
            )}
            {selectedLetter && (
              <Chip
                label={`Letter: ${selectedLetter}`}
                onDelete={() => setSelectedLetter(null)}
                color="primary"
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Alphabetical Index */}
      {firstLetters.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Browse by Letter:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {firstLetters.map((letter) => (
              <Button
                key={letter}
                variant={selectedLetter === letter ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleLetterClick(letter)}
                sx={{ minWidth: 40 }}
              >
                {letter}
              </Button>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Entries List */}
      {loading ? (
        <Typography>Loading...</Typography>
      ) : entries.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Book sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No glossary entries found
          </Typography>
          {isSuperAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddClick}
              sx={{ mt: 2 }}
            >
              Add First Entry
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={2}>
            {entries.map((entry) => (
              <Grid item xs={12} md={6} lg={4} key={entry._id}>
                <Card>
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleViewDetails(entry._id)}>
                          {entry.term}
                        </Typography>
                        {isSuperAdmin && (
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => handleEditClick(entry)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteClick(entry)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {truncateText(entry.definition)}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {entry.context?.map((ctx) => (
                          <Chip
                            key={ctx}
                            label={ctx}
                            size="small"
                            color={getContextColor(ctx, contexts)}
                          />
                        ))}
                      </Stack>
                      <Button
                        size="small"
                        onClick={() => handleViewDetails(entry._id)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        View Details
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">{selectedEntry?.term}</Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Definition
                </Typography>
                <Typography variant="body1">{selectedEntry.definition}</Typography>
              </Box>
              {selectedEntry.context && selectedEntry.context.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Context
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedEntry.context.map((ctx) => (
                      <Chip
                        key={ctx}
                        label={ctx}
                        color={getContextColor(ctx, contexts)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              {selectedEntry.example && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Example
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {selectedEntry.example}
                  </Typography>
                </Box>
              )}
              {selectedEntry.source && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Source
                  </Typography>
                  <Typography variant="body2">{selectedEntry.source}</Typography>
                </Box>
              )}
              {selectedEntry.relatedTerms && selectedEntry.relatedTerms.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Related Terms
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {selectedEntry.relatedTerms.map((term) => (
                      <Chip key={term} label={term} size="small" />
                    ))}
                  </Stack>
                </Box>
              )}
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created by {selectedEntry.createdBy?.name || 'Unknown'} on{' '}
                  {new Date(selectedEntry.createdAt).toLocaleDateString()}
                  {selectedEntry.updatedBy && (
                    <> • Updated by {selectedEntry.updatedBy.name} on {new Date(selectedEntry.updatedAt).toLocaleDateString()}</>
                  )}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {isSuperAdmin && selectedEntry && (
            <>
              <Button onClick={() => {
                setDetailDialogOpen(false);
                handleEditClick(selectedEntry);
              }}>
                Edit
              </Button>
              <Button onClick={() => {
                setDetailDialogOpen(false);
                handleDeleteClick(selectedEntry);
              }} color="error">
                Delete
              </Button>
            </>
          )}
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {selectedEntry ? 'Edit Glossary Entry' : 'Add Glossary Entry'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Term"
              value={formData.term}
              onChange={(e) => setFormData({ ...formData, term: e.target.value })}
              fullWidth
              required
              error={!!formErrors.term}
              helperText={formErrors.term}
            />
            <TextField
              label="Definition"
              value={formData.definition}
              onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
              fullWidth
              required
              multiline
              rows={4}
              error={!!formErrors.definition}
              helperText={formErrors.definition}
            />
            <Autocomplete
              multiple
              options={contextOptions}
              value={formData.context}
              onChange={(event, newValue) => setFormData({ ...formData, context: newValue })}
              loading={contextsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Context"
                  required
                  error={!!formErrors.context}
                  helperText={formErrors.context || 'Select at least one context'}
                />
              )}
            />
            <TextField
              label="Example (Optional)"
              value={formData.example}
              onChange={(e) => setFormData({ ...formData, example: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Source (Optional)"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value })}
              fullWidth
              placeholder="e.g., DGCA CAR Section 7, Series J"
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Related Terms (Optional)
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add related term"
                  value={relatedTermInput}
                  onChange={(e) => setRelatedTermInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddRelatedTerm();
                    }
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Button onClick={handleAddRelatedTerm}>Add</Button>
              </Stack>
              {formData.relatedTerms.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {formData.relatedTerms.map((term) => (
                    <Chip
                      key={term}
                      label={term}
                      onDelete={() => handleRemoveRelatedTerm(term)}
                      size="small"
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setSelectedEntry(null);
            setFormErrors({});
          }}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained">
            {selectedEntry ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Glossary Entry</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete the entry "{entryToDelete?.term}"? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Management Dialog */}
      {isSuperAdmin && (
        <>
          {/* Context List Dialog */}
          <Dialog 
            open={contextDialogOpen && !contextFormOpen} 
            onClose={() => setContextDialogOpen(false)} 
            maxWidth="md" 
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Manage Contexts</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddNewContext}
                  size="small"
                >
                  Add Context
                </Button>
              </Stack>
            </DialogTitle>
            <DialogContent>
              {contexts.length === 0 ? (
                <Alert severity="info">No contexts found. Add your first context to get started.</Alert>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {contexts.map((context) => (
                    <Card key={context._id} variant="outlined">
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Chip label={context.name} color={context.color || 'default'} />
                            <Typography variant="body2" color="text.secondary">
                              Order: {context.order}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" onClick={() => handleEditContextClick(context)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteContextClick(context)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setContextDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Context Form Dialog */}
          <Dialog 
            open={contextFormOpen} 
            onClose={() => {
              setContextFormOpen(false);
              setEditingContext(null);
              setContextFormData({ name: '', color: 'default', order: 0 });
            }} 
            maxWidth="sm" 
            fullWidth
          >
            <DialogTitle>
              {editingContext ? 'Edit Context' : 'Add Context'}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Context Name"
                  value={contextFormData.name}
                  onChange={(e) => setContextFormData({ ...contextFormData, name: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Aircraft, Operations, etc."
                />
                <Autocomplete
                  options={['default', 'primary', 'secondary', 'success', 'error', 'warning', 'info']}
                  value={contextFormData.color}
                  onChange={(event, newValue) => setContextFormData({ ...contextFormData, color: newValue || 'default' })}
                  renderInput={(params) => (
                    <TextField {...params} label="Color" />
                  )}
                />
                <TextField
                  label="Order"
                  type="number"
                  value={contextFormData.order}
                  onChange={(e) => setContextFormData({ ...contextFormData, order: parseInt(e.target.value) || 0 })}
                  fullWidth
                  helperText="Lower numbers appear first in the list"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setContextFormOpen(false);
                setEditingContext(null);
                setContextFormData({ name: '', color: 'default', order: 0 });
              }}>
                Cancel
              </Button>
              <Button onClick={handleContextFormSubmit} variant="contained">
                {editingContext ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Context Confirmation Dialog */}
          <Dialog 
            open={!!deletingContext} 
            onClose={() => setDeletingContext(null)}
          >
            <DialogTitle>Delete Context</DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to delete the context "{deletingContext?.name}"? 
                {deletingContext && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    This action cannot be undone. The context will be removed from the list, but existing glossary entries using this context will not be affected.
                  </Typography>
                )}
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeletingContext(null)}>Cancel</Button>
              <Button onClick={handleDeleteContextConfirm} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Glossary;
